// Asset preview — FX tab: buttons that trigger every FxApi effect and telegraph
// on a test stage through createFx with a minimal context, plus a gallery of
// the shared FX / projectile textures.

import Phaser from 'phaser';
import { audio } from '../../audio';
import { COLORS } from '../../config/palette';
import type { FxApi, GameContext, TimeApi } from '../../core/contracts';
import { bus } from '../../core/events';
import { Rng } from '../../core/rng';
import { save } from '../../core/save';
import type { TrailKind } from '../../core/types';
import { createFx } from '../../systems/fx';
import { Gallery, texturesWithPrefix } from './gallery';
import type { PreviewTab } from './tab';
import { Button, PV, drawPanel, label, sectionTitle } from './widgets';

type FxAction = [name: string, run: (fx: FxApi, at: { x: number; y: number; tx: number; ty: number }) => void];

const TRAILS: TrailKind[] = ['electric', 'burn', 'plasma', 'void'];

const ACTIONS: { title: string; items: FxAction[] }[] = [
  {
    title: 'WEAPONS / IMPACTS',
    items: [
      ['MUZZLE FLASH', (fx, a) => fx.muzzleFlash(a.x, a.y, 0, COLORS.volt, 1)],
      ['SPARKS', (fx, a) => fx.sparks(a.tx, a.ty, Math.PI, { count: 14 })],
      ['IMPACT BULLET', (fx, a) => fx.impact(a.tx, a.ty, Math.PI, 'bullet')],
      ['IMPACT HEAVY', (fx, a) => fx.impact(a.tx, a.ty, Math.PI, 'heavy')],
      ['IMPACT ENERGY', (fx, a) => fx.impact(a.tx, a.ty, Math.PI, 'energy', COLORS.electric)],
      ['IMPACT SHIELD', (fx, a) => fx.impact(a.tx, a.ty, Math.PI, 'shield')],
      ['BEAM', (fx, a) => fx.beam(a.x, a.y, a.tx, a.ty, { color: COLORS.electric, width: 10, duration: 260 })],
      ['VENT', (fx, a) => fx.vent(a.x, a.y - 20, -Math.PI / 2)],
    ],
  },
  {
    title: 'BLASTS / ELEMENTS',
    items: [
      ['EXPLOSION S', (fx, a) => fx.explosion(a.tx, a.ty, 60)],
      ['EXPLOSION L', (fx, a) => fx.explosion(a.tx, a.ty, 160, COLORS.threatHot)],
      ['LIGHTNING', (fx, a) => fx.lightning([{ x: a.x, y: a.y }, { x: (a.x + a.tx) / 2, y: a.y - 90 }, { x: a.tx, y: a.ty }], { color: COLORS.electric, branches: true })],
      ['RING', (fx, a) => fx.ring(a.tx, a.ty, 140, { color: COLORS.shield, fromRadius: 10 })],
      ['SMOKE', (fx, a) => fx.smoke(a.tx, a.ty, { count: 8 })],
      ['EMBERS', (fx, a) => fx.embers(a.tx, a.ty, { count: 16 })],
      ['DEBRIS', (fx, a) => fx.debris(a.tx, a.ty, { count: 12 })],
      ['TRAILS ×4', (fx, a) => TRAILS.forEach((k, i) => fx.trailSegment(a.x - 200, a.y + 40 + i * 26, a.tx, a.y + 40 + i * 26, k, 14))],
    ],
  },
  {
    title: 'NUMBERS / SCREEN',
    items: [
      ['DAMAGE 42', (fx, a) => fx.damageNumber(a.tx, a.ty - 40, 42)],
      ['CRIT 128', (fx, a) => fx.damageNumber(a.tx, a.ty - 40, 128, { crit: true })],
      ['PLAYER HIT', (fx, a) => fx.damageNumber(a.x, a.y - 60, 17, { player: true })],
      ['FLOAT TEXT', (fx, a) => fx.floatText(a.x, a.y - 120, 'SHIELD BREAK', { color: COLORS.shield, size: 28 })],
      ['SHAKE', (fx) => fx.shake(0.008, 260)],
      ['FLASH', (fx) => fx.flash(0xffffff, 0.5, 160)],
    ],
  },
  {
    title: 'TELEGRAPHS',
    items: [
      ['WARN LINE', (fx, a) => void fx.warnLine(a.tx, a.ty - 140, a.x - 300, a.y, 1100, { width: 16 })],
      ['WARN CIRCLE', (fx, a) => void fx.warnCircle(a.tx, a.ty, 150, 1200)],
      ['WARN RECT', (fx, a) => void fx.warnRect(a.x - 260, a.y - 60, 520, 120, 1200)],
      ['WARN MARKER', (fx, a) => void fx.warnMarker(a.tx, a.ty - 120, 1000)],
      ['SPAWN PORTAL', (fx, a) => fx.spawnPortal(a.tx, a.ty, 1200)],
    ],
  },
];

export class FxTab implements PreviewTab {
  readonly root: Phaser.GameObjects.Container;
  private fx: FxApi | null = null;
  private status: Phaser.GameObjects.Text;
  private gallery: Gallery;
  private auto = false;
  private autoBtn: Button;
  private autoTimer = 0;
  private autoIndex = 0;
  private clock = 0;
  private readonly at: { x: number; y: number; tx: number; ty: number };

  constructor(private scene: Phaser.Scene) {
    this.root = scene.add.container(0, 0);
    const g = scene.add.graphics();
    this.root.add(g);
    const sx = PV.pad;
    const sw = PV.sideW + 120;
    drawPanel(g, sx, PV.headerH + 12, sw, PV.H - PV.headerH - 32, { fill: COLORS.uiPanel, fillAlpha: 0.85, chamfer: 14, accent: COLORS.electric });

    // Stage: the area right of the panel with a floor line, an emitter and a target dummy.
    const stX = sx + sw + PV.pad;
    const stY = PV.headerH + 12;
    const stW = PV.W - PV.pad - stX;
    const stH = 600;
    drawPanel(g, stX, stY, stW, stH, { fill: 0x070a10, fillAlpha: 1, chamfer: 12, accent: COLORS.electric });
    const floor = stY + stH - 110;
    g.lineStyle(2, COLORS.steel3, 1);
    g.lineBetween(stX + 20, floor, stX + stW - 20, floor);
    for (let x = stX + 40; x < stX + stW - 20; x += 48) {
      g.lineStyle(1, COLORS.steel1, 1);
      g.lineBetween(x, floor + 2, x, floor + 40);
    }
    this.at = { x: stX + stW * 0.3, y: floor - 60, tx: stX + stW * 0.7, ty: floor - 60 };
    // Emitter + dummy silhouettes.
    g.fillStyle(COLORS.steel2, 1).fillRoundedRect(this.at.x - 20, floor - 88, 40, 88, 6);
    g.lineStyle(2, COLORS.volt, 0.8).strokeRoundedRect(this.at.x - 20, floor - 88, 40, 88, 6);
    g.fillStyle(COLORS.steel2, 1).fillRoundedRect(this.at.tx - 26, floor - 96, 52, 96, 6);
    g.lineStyle(2, COLORS.threat, 0.8).strokeRoundedRect(this.at.tx - 26, floor - 96, 52, 96, 6);
    this.root.add(label(scene, stX + 18, stY + 14, 'FX STAGE · createFx()', { font: 'display', size: 15, weight: 700, spacing: 3 }));

    // Buttons.
    let y = PV.headerH + 30;
    const bw = (sw - 40) / 2;
    for (const group of ACTIONS) {
      this.root.add(sectionTitle(scene, sx + 26, y, group.title, COLORS.electric));
      y += 22;
      group.items.forEach(([name, run], i) => {
        const b = new Button(scene, sx + 16 + (i % 2) * (bw + 8), y + Math.floor(i / 2) * 38, name, () => this.trigger(name, run), {
          w: bw,
          h: 32,
          accent: COLORS.electric,
          size: 13,
        });
        this.root.add(b.root);
      });
      y += Math.ceil(group.items.length / 2) * 38 + 14;
    }
    this.autoBtn = new Button(scene, sx + 16, y, 'AUTO DEMO', () => this.setAuto(!this.auto), { w: sw - 32, h: 34, accent: COLORS.electric, toggle: true, hint: 'SPACE' });
    this.root.add(this.autoBtn.root);
    this.status = label(scene, sx + 16, y + 46, '', { font: 'mono', size: 12, color: COLORS.uiDim });
    this.root.add(this.status);

    // Texture gallery below the stage.
    const gy = stY + stH + 12;
    const entries = texturesWithPrefix(scene, ['fx_', 'proj_']).map((k) => ({ key: k, label: k, add: k.startsWith('fx_') }));
    this.gallery = new Gallery(scene, entries, { x: stX, y: gy, w: stW, h: PV.H - PV.pad - gy, cellW: 118, cellH: 112, maxScale: 1, accent: COLORS.electric, title: 'FX + PROJECTILE TEXTURES' });
    this.root.add(this.gallery.root);
  }

  /** Lazily creates the FX system (it may still be a stub that throws). */
  private ensureFx(): FxApi | null {
    if (this.fx) return this.fx;
    const clock = { now: 0 };
    const time: TimeApi = {
      get now() {
        return clock.now;
      },
      hitstop() {},
      inHitstop: false,
      slowMo() {},
      scale: 1,
    };
    const self = this;
    const ctx = {
      scene: this.scene,
      bus,
      rng: new Rng(1337),
      get settings() {
        return save.settings;
      },
      audio,
      get time() {
        clock.now = self.clock;
        return time;
      },
    } as unknown as GameContext;
    try {
      const fx = createFx(ctx);
      ctx.fx = fx;
      fx.start?.();
      this.fx = fx;
      this.status.setText('createFx ready');
      return fx;
    } catch (err) {
      this.status.setText(`createFx unavailable:\n${String((err as Error)?.message ?? err)}`);
      return null;
    }
  }

  private trigger(name: string, run: FxAction[1]): void {
    const fx = this.ensureFx();
    if (!fx) return;
    try {
      run(fx, this.at);
      this.status.setText(name);
    } catch (err) {
      this.status.setText(`${name} failed:\n${String((err as Error)?.message ?? err)}`);
    }
  }

  private setAuto(on: boolean): void {
    this.auto = on;
    this.autoBtn.setActive(on);
  }

  update(dt: number): void {
    this.clock += dt;
    if (this.auto) {
      this.autoTimer -= dt;
      if (this.autoTimer <= 0) {
        this.autoTimer = 0.55;
        const all = ACTIONS.flatMap((g) => g.items).filter(([n]) => n !== 'SHAKE' && n !== 'FLASH');
        const [name, run] = all[this.autoIndex++ % all.length];
        this.trigger(name, run);
      }
    }
    if (this.fx) {
      try {
        this.fx.update(dt);
      } catch (err) {
        this.status.setText(`fx.update failed: ${String((err as Error)?.message ?? err)}`);
        this.fx = null;
      }
    }
  }

  key(code: string): boolean {
    if (code === 'Space') {
      this.setAuto(!this.auto);
      return true;
    }
    return false;
  }

  destroy(): void {
    try {
      this.fx?.destroy();
    } catch {
      // The scene may already have destroyed the fx objects.
    }
    this.fx = null;
    this.root.destroy();
  }
}
